import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Grid,
  Typography,
  CircularProgress,
} from '@mui/material';
import { db } from '../../firebase';
import { collection, query, where, onSnapshot, doc, getDoc, getDocs, Timestamp } from 'firebase/firestore';

export default function Home({ unitCode, companyCode, unitType, unitName, isPureVeg, userId }) {
  const navigate = useNavigate();
  const location = useLocation();
  const derivedCompanyCode = location?.state?.companyCode || 
                            (location?.search ? new URLSearchParams(location.search).get('code') : null) || 
                            sessionStorage.getItem('companyCode') || null;
  const effectiveCompanyCode = companyCode || derivedCompanyCode;
  const [orderStats, setOrderStats] = useState({
    totalOrders: 0,
    confirmedOrders: 0,
    preparingOrders: 0,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [message, setMessage] = useState(null);
  const [assignedItemIds, setAssignedItemIds] = useState([]);

  const isFoodcourt = unitType?.toLowerCase() === 'foodcourt';
  const isHotel = unitType?.toLowerCase() === 'hotel';
  const dashboardType = isFoodcourt ? 'Foodcourt Kitchen' : isHotel ? 'Hotel Kitchen' : 'Restaurant Kitchen';

  const fetchUnitData = async () => {
    if (!unitCode || !effectiveCompanyCode) {
      setMessage('Missing unit code or company code. Please ensure you are logged in correctly.');
      console.error('KitchenHome: No unitCode or companyCode for fetching unit data', { unitCode, userId, companyCode: effectiveCompanyCode });
      setIsLoading(false);
      return;
    }

    try {
      const unitDocRef = doc(db, 'units', unitCode);
      const unitDoc = await getDoc(unitDocRef);
      if (unitDoc.exists()) {
        const unitData = unitDoc.data();
        if (unitData.companyCode !== effectiveCompanyCode) {
          console.warn('KitchenHome: Company code mismatch', { 
            firestoreCompanyCode: unitData.companyCode, 
            providedCompanyCode: effectiveCompanyCode 
          });
          setMessage('Company code mismatch. Please log in again.');
          setIsLoading(false);
          return;
        }
        console.log('KitchenHome: Unit details fetched:', {
          unitCode,
          companyCode: unitData.companyCode,
          unitType: unitData.unitType,
          unitName: unitData.unitName,
          isPureVeg: unitData.isPureVeg,
        });
      } else {
        console.error('KitchenHome: No unit found for unitCode:', unitCode);
        setMessage('Unit not found. Please check your unit code.');
        setIsLoading(false);
      }
    } catch (err) {
      console.error('KitchenHome: Error fetching unit details:', { err, unitCode, userId, companyCode: effectiveCompanyCode });
      setMessage(`Failed to load unit details: ${err.message}`);
      setIsLoading(false);
    }
  };

  const fetchAssignedItemIds = async () => {
    if (!unitCode || !userId || !effectiveCompanyCode) {
      setMessage('Missing unit code, user ID, or company code. Please ensure you are logged in correctly.');
      console.error('KitchenHome: Missing required data', { unitCode, userId, companyCode: effectiveCompanyCode });
      setIsLoading(false);
      return [];
    }

    try {
      console.log('KitchenHome: Fetching assigned menu items from companymenu', { unitCode, userId, companyCode: effectiveCompanyCode });
      const menuQuery = query(
        collection(db, 'companymenu'),
        where('unitCode', '==', unitCode),
        where('companyCode', '==', effectiveCompanyCode),
        where('staff', '==', userId)
      );
      const menuSnapshot = await getDocs(menuQuery);
      const itemIds = menuSnapshot.docs.map(doc => doc.id);
      console.log('KitchenHome: Assigned menu items:', { itemIds, unitCode, userId, companyCode: effectiveCompanyCode, documentCount: menuSnapshot.size });
      if (itemIds.length === 0) {
        setMessage('No menu items assigned to you.');
        setOrderStats({ totalOrders: 0, confirmedOrders: 0, preparingOrders: 0 });
        setIsLoading(false);
      }
      return itemIds;
    } catch (err) {
      console.error('KitchenHome: Error fetching assigned menu items:', { err, unitCode, userId, companyCode: effectiveCompanyCode });
      setMessage(`Failed to load assigned menu items: ${err.message}`);
      setOrderStats({ totalOrders: 0, confirmedOrders: 0, preparingOrders: 0 });
      setIsLoading(false);
      return [];
    }
  };

  useEffect(() => {
    console.log('KitchenHome: Received props:', { unitCode, companyCode: effectiveCompanyCode, unitType, unitName, isPureVeg, userId });
    if (!unitCode || !userId || !effectiveCompanyCode) {
      setMessage('Missing unit code, company code, or user ID. Please ensure you are logged in correctly.');
      console.error('KitchenHome: Missing required data', { unitCode, companyCode: effectiveCompanyCode, userId });
      setIsLoading(false);
      return;
    }

    if (unitCode && effectiveCompanyCode && userId) {
      sessionStorage.setItem('unitCode', unitCode);
      sessionStorage.setItem('companyCode', effectiveCompanyCode);
      sessionStorage.setItem('userId', userId);
      sessionStorage.setItem('unitType', unitType || 'Unknown');
      sessionStorage.setItem('unitName', unitName || 'Unknown Company');
      sessionStorage.setItem('isPureVeg', isPureVeg ? 'true' : 'false');
    }

    const setupRealtimeListener = async () => {
      setIsLoading(true);
      await fetchUnitData();
      const itemIds = await fetchAssignedItemIds();
      setAssignedItemIds(itemIds);

      if (itemIds.length === 0) {
        return;
      }

      const todayStart = new Date();
      todayStart.setHours(0, 0, 0, 0);
      const todayEnd = new Date();
      todayEnd.setHours(23, 59, 59, 999);

      const ordersQuery = query(
        collection(db, 'orders'),
        where('unitCode', '==', unitCode),
        where('companyCode', '==', effectiveCompanyCode)
      );

      const unsubscribe = onSnapshot(ordersQuery, (snapshot) => {
        try {
          console.log('KitchenHome: Received snapshot update for orders', { unitCode, userId, companyCode: effectiveCompanyCode });
          const orders = snapshot.docs
            .filter(doc => {
              const createdAt = doc.data().createdAt;
              if (createdAt instanceof Timestamp) {
                const date = createdAt.toDate();
                return date >= todayStart && date <= todayEnd;
              }
              return false;
            })
            .map(doc => ({
              id: doc.id,
              ...doc.data(),
              items: doc.data().items.filter(item => itemIds.includes(item.id)),
            }))
            .filter(order => order.items.length > 0);

          const totalOrders = orders.length;
          const confirmedOrders = orders.filter(order =>
            order.items.some(item => item.status === 'Confirmed')
          ).length;
          const preparingOrders = orders.filter(order =>
            order.items.some(item => item.status === 'Preparing')
          ).length;

          setOrderStats({
            totalOrders,
            confirmedOrders,
            preparingOrders,
          });
          console.log('KitchenHome: Updated order stats:', {
            totalOrders,
            confirmedOrders,
            preparingOrders,
            unitCode,
            unitType,
            userId,
            companyCode: effectiveCompanyCode,
          });

          setIsLoading(false);
        } catch (err) {
          console.error('KitchenHome: Error processing snapshot:', { err, unitCode, unitType, userId, companyCode: effectiveCompanyCode });
          setMessage(`Failed to update order statistics: ${err.message}`);
          setOrderStats({ totalOrders: 0, confirmedOrders: 0, preparingOrders: 0 });
          setIsLoading(false);
        }
      }, (err) => {
        console.error('KitchenHome: Snapshot listener error:', { err, unitCode, unitType, userId, companyCode: effectiveCompanyCode });
        setMessage(`Failed to listen for order updates: ${err.message}`);
        setOrderStats({ totalOrders: 0, confirmedOrders: 0, preparingOrders: 0 });
        setIsLoading(false);
      });

      return unsubscribe;
    };

    setupRealtimeListener().then(unsubscribe => {
      return unsubscribe;
    });

    return () => {
      // Cleanup handled by unsubscribe function
    };
  }, [unitCode, effectiveCompanyCode, unitType, userId]);

  const handleViewOrders = () => {
    console.log('Home: Navigating to /kitchen_staff/orders', { unitCode, companyCode, unitType, unitName, isPureVeg, userId });
    if (!unitCode || !companyCode || !userId) {
      console.error('Home: Cannot navigate to orders, missing required data', { unitCode, companyCode, userId });
      return;
    }
    sessionStorage.setItem('unitCode', unitCode);
    sessionStorage.setItem('companyCode', companyCode);
    sessionStorage.setItem('unitType', unitType || 'Unknown');
    sessionStorage.setItem('companyName', unitName || 'Unknown Company');
    sessionStorage.setItem('isPureVeg', isPureVeg ? 'true' : 'false');
    sessionStorage.setItem('userId', userId);
    navigate(`/kitchen_staff/orders?unit=${unitCode}&code=${companyCode}&type=${unitType || 'Unknown'}&name=${encodeURIComponent(unitName || 'Unknown Company')}&isPureVeg=${isPureVeg || false}`, {
      state: { unitCode, companyCode, unitType, companyName: unitName, isPureVeg, userId },
    });
  };

  if (isLoading) {
    return (
      <Container maxWidth="md" sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Loading {dashboardType.toLowerCase()} dashboard...
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom>
        {unitName ? `${unitName} ${dashboardType} Dashboard` : `${dashboardType} Dashboard`}
      </Typography>
      {message && (
        <Typography variant="h6" color="text.secondary" sx={{ textAlign: 'center', mb: 3 }}>
          {message}
        </Typography>
      )}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={4}>
          <Card
            elevation={3}
            sx={{
              textAlign: 'center',
              transition: 'transform 0.2s',
              '&:hover': { transform: 'scale(1.05)' },
            }}
          >
            <CardContent>
              <Typography variant="h6" color="text.secondary">
                Today's Orders
              </Typography>
              <Typography variant="h4" color="primary" sx={{ mt: 1 }}>
                {orderStats.totalOrders}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card
            elevation={3}
            sx={{
              textAlign: 'center',
              transition: 'transform 0.2s',
              '&:hover': { transform: 'scale(1.05)' },
            }}
          >
            <CardContent>
              <Typography variant="h6" color="text.secondary">
                Confirmed Orders
              </Typography>
              <Typography variant="h4" color="primary" sx={{ mt: 1 }}>
                {orderStats.confirmedOrders}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card
            elevation={3}
            sx={{
              textAlign: 'center',
              transition: 'transform 0.2s',
              '&:hover': { transform: 'scale(1.05)' },
            }}
          >
            <CardContent>
              <Typography variant="h6" color="text.secondary">
                Preparing Orders
              </Typography>
              <Typography variant="h4" color="primary" sx={{ mt: 1 }}>
                {orderStats.preparingOrders}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleViewOrders}
          sx={{ minWidth: 150 }}
          disabled={!unitCode || !effectiveCompanyCode || !userId}
        >
          View Orders
        </Button>
      </Box>
    </Container>
  );
}